import { motion } from "framer-motion";
import { toast } from "sonner";
import { API_URL } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import Countdown from "@/components/Countdown";
import PressForm from "@/components/PressForm";

const EASE = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export default function PressPage() {
  const { t } = useLanguage();

  const copyRelease = async () => {
    try {
      await navigator.clipboard.writeText(t("press.releaseBody"));
      toast.success(t("press.copied"));
    } catch {
      toast.error(t("common.error"));
    }
  };

  const facts = [
    ["500.000", t("press.fact1")],
    ["$1", t("press.fact2")],
    [t("press.fact3v"), t("press.fact3")],
    ["400 × 400", t("press.fact4")],
    ["GEN 2027", t("press.fact5")],
    ["IT / EN", t("press.fact6")],
  ];

  return (
    <main className="min-h-screen bg-[#FAF8F5] pt-[57px]" data-testid="press-page">
      {/* HERO */}
      <section className="px-5 md:px-12 pt-20 pb-14 border-b border-border/60">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          className="text-[10px] tracking-[0.4em] text-golddark mb-6"
        >
          {t("press.eyebrow").toUpperCase()}
        </motion.p>
        <h1 className="font-display font-black tracking-tighter leading-[0.9] text-[11vw] md:text-[7vw]" data-testid="press-title">
          <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 1, delay: 0.2, ease: EASE }}>
              {t("press.title")}
            </motion.span>
          </span>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
          className="mt-8 text-sm md:text-base text-muted-foreground max-w-2xl leading-relaxed"
        >
          {t("press.sub")}
        </motion.p>
      </section>

      {/* COUNTDOWN */}
      <section className="px-5 md:px-12 py-16 border-b border-border/60 text-center">
        <Reveal>
          <p className="text-[10px] tracking-[0.4em] text-muted-foreground mb-8">{t("countdown.label")}</p>
          <Countdown />
        </Reveal>
      </section>

      {/* FACTS */}
      <section className="border-b border-border/60">
        <div className="px-5 md:px-12 pt-16 pb-8">
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] text-golddark">{t("press.factsTitle").toUpperCase()}</p>
          </Reveal>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3">
          {facts.map(([num, label], i) => (
            <Reveal key={num} delay={i * 0.08} className="border-t border-r border-border/60 px-6 md:px-10 py-10">
              <div className="font-mono text-3xl md:text-4xl font-light text-golddark tabular-nums" data-testid={`press-fact-${i}`}>
                {num}
              </div>
              <div className="mt-3 text-[10px] tracking-[0.25em] text-muted-foreground leading-relaxed">{label.toUpperCase()}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRESS RELEASE */}
      <section className="px-5 md:px-12 py-20 border-b border-border/60">
        <div className="max-w-3xl">
          <Reveal>
            <div className="flex items-baseline justify-between flex-wrap gap-4 mb-8">
              <h2 className="font-display font-black tracking-tighter text-3xl md:text-5xl">{t("press.releaseTitle")}</h2>
              <span className="text-[10px] tracking-[0.3em] text-golddark">{t("press.releaseDate").toUpperCase()}</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="border border-border/60 p-8 md:p-12">
              <p className="font-display italic text-xl md:text-2xl leading-snug text-[#3C3735]" data-testid="press-release-headline">
                "{t("press.releaseHeadline")}"
              </p>
              <p className="mt-8 text-sm text-muted-foreground leading-[1.9] whitespace-pre-line" data-testid="press-release-body">
                {t("press.releaseBody")}
              </p>
              <button
                onClick={copyRelease}
                data-testid="press-copy-release-btn"
                className="mt-10 px-8 py-3 border border-gold/50 text-golddark text-[10px] tracking-[0.25em] hover:bg-[#7D8C77] hover:text-[#FAF8F5] transition-colors duration-200"
              >
                {t("press.copy").toUpperCase()}
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ASSETS + CONTACT */}
      <section className="px-5 md:px-12 py-20 grid md:grid-cols-2 gap-4">
        <Reveal className="border border-border/60 p-8 md:p-10">
          <h3 className="font-display font-bold tracking-tight text-2xl mb-8">{t("press.assetsTitle")}</h3>
          <div className="space-y-3">
            <a
              href={`${API_URL}/mosaic.jpg`}
              download="mosaico-gandhi-in-formazione.jpg"
              data-testid="press-asset-mosaic"
              className="block px-6 py-4 border border-border text-[10px] tracking-[0.25em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
            >
              {t("press.assetMosaic").toUpperCase()} ↓
            </a>
            <a
              href="/certificato/123456"
              data-testid="press-asset-cert"
              className="block px-6 py-4 border border-border text-[10px] tracking-[0.25em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
            >
              {t("press.assetCert").toUpperCase()} →
            </a>
            <a
              href="/"
              data-testid="press-asset-site"
              className="block px-6 py-4 border border-border text-[10px] tracking-[0.25em] hover:bg-[#3C3735] hover:text-[#FAF8F5] transition-colors duration-200"
            >
              {t("press.assetSite").toUpperCase()} →
            </a>
          </div>
        </Reveal>
        <Reveal delay={0.15} className="border border-gold/40 p-8 md:p-10 flex flex-col justify-between">
          <div>
            <h3 className="font-display font-bold tracking-tight text-2xl mb-4">{t("press.contactTitle")}</h3>
            <p className="text-xs text-muted-foreground leading-relaxed mb-8">{t("press.contactNote")}</p>
          </div>
          <a
            href="mailto:info@marcoorlandi.it"
            data-testid="press-contact-email"
            className="font-mono text-lg md:text-xl text-golddark hover:text-[#3C3735] transition-colors duration-200 break-all"
          >
            info@marcoorlandi.it
          </a>
        </Reveal>
      </section>

      {/* MEDIA LIST FORM */}
      <PressForm />

      {/* QUOTE */}
      <section className="px-5 md:px-12 py-24 border-t border-border/60 text-center">
        <Reveal>
          <p className="font-display italic text-2xl md:text-4xl leading-snug max-w-4xl mx-auto" data-testid="press-quote">
            "{t("press.quote")}"
          </p>
          <p className="mt-6 text-[10px] tracking-[0.35em] text-muted-foreground">— M. K. GANDHI</p>
        </Reveal>
      </section>
    </main>
  );
}
